import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import reorderArrayElement from 'common/util/reorderArrayElement';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';

import {
  TOUR_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_STATUS_UNREAD,
  TOUR_TYPE_SELF_SHOWING,
} from 'common/tourConstants';

import { getTourShowingLabel } from 'common/tourHelpers';

import { getApplicationMovePeriodFormat } from 'common/householdApplication/applicationHelpers';

import { LISTING_DATA_SOURCE_SMART_APARTMENT } from 'common/listingConstants';

import { getListingSmartApartmentLink } from 'common/listingHelpers';
import {
  isTourUserPrequalCompleted,
  getShowingDisqualifications,
} from 'client/components/SrAdminTourCard/tourCardHelpers';
import {
  SR_API_ACTION_ADMIN_UNSCHEDULE_SHOWING,
  SR_API_ACTION_ADMIN_UPDATE_SHOWING_POSITIONS,
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_APPLY_TOUR_STATE_CHANGE,
  SR_API_ACTION_CALCULATE_TOUR_ROUTE,
} from 'api/actionTypes';

import { DATA_TABLE_SOURCE_ADMIN_TOURS } from 'common/dataTableConstants';

import SrLink from 'client/components/SrLink/SrLink';
import SrTourRescheduleLink from 'client/components/SrTourRescheduleLink';
import SrTourCopyMlsNumber from 'client/components/SrTourCopyMlsNumber';
import SrTourCopyAllMlsNumbersHeader from 'client/components/SrTourCopyAllMlsNumbersHeader';
import SrTourCopyAllAddressesHeader from 'client/components/SrTourCopyAllAddressesHeader';
import SrAdminTourCardDeleteDropdown from 'client/components/SrAdminTourCard/SrAdminTourCardDeleteDropdown';
import SrAdminTourNotes from 'client/components/SrAdminTourCard/SrAdminTourNotes';
import SrAdminTourCardSelectTourAgent from 'client/components/SrAdminTourCard/SrAdminTourCardSelectTourAgent';
import SrAdminTourCardSelectStartDate from 'client/components/SrAdminTourCard/SrAdminTourCardSelectStartDate';
import SrAdminTourCardRoute from 'client/components/SrAdminTourCard/SrAdminTourCardRoute';
import SrAdminTourCardListingCell from 'client/components/SrAdminTourCard/SrAdminTourCardListingCell';
import SrAssignHouseholdAdminSelect from 'client/components/SrAssignHouseholdAdminSelect';
import SrHouseholdNotesContainer from 'client/components/SrHouseholdNotesContainer';
import SrBadge from 'client/components/SrBadge';
import SrCopyText from 'client/components/SrCopyText';
import SrFrontConversation from 'client/components/SrFrontConversation';

import postWithState from 'client/actions/postWithState';
import { actionDataTableRemoveItem } from 'client/helpers/dataTables';
import withApiState from 'client/helpers/withApiState';

import branch from 'client/core/branch';

const activeShowingStatuses = [TOUR_SHOWING_STATUS_COMPLETED, TOUR_SHOWING_STATUS_FAILED];

@withApiState((props) => ({
  updateShowingPositionApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_SHOWING_POSITIONS,
    queryId: `${props.tour.id}`,
  },
  calculateTourRouteApiState: {
    action: SR_API_ACTION_CALCULATE_TOUR_ROUTE,
    queryId: `${props.tour.id}`,
  },
}))
@branch({
  tourAgents: ['tourAgents'],
  householdAdminsList: ['householdAdminsList'],
})
class SrAdminTourCardScheduled extends Component {
  static propTypes = {
    tour: PropTypes.object.isRequired,
    tourAgents: PropTypes.array.isRequired,
    reloadDataTableTour: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    updateShowingPositionApiState: PropTypes.object.isRequired,
    calculateTourRouteApiState: PropTypes.object.isRequired,
    onMakeAvailableShowingButtonClick: PropTypes.func.isRequired,
    onNotAvailableShowingButtonClick: PropTypes.func.isRequired,
    onShowingMarkReadButtonClick: PropTypes.func.isRequired,
    onOpenShowingNotes: PropTypes.func.isRequired,
    onOpenMessageTenant: PropTypes.func.isRequired,
    onOpenRenterPrequal: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    householdAdminsList: PropTypes.array,
  };

  constructor(props) {
    super(props);
    this.state = {
      newShowingsOrder: null,
      tourRoute: null,
    };
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onShowingCompletedButtonClick = ({ listingId }) => {
    const { tour, reloadDataTableTour, dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'tourListings',
        where: {
          tourId: tour.id,
          listingId: listingId,
        },
        updates: {
          status: TOUR_SHOWING_STATUS_COMPLETED,
        },
      },
    }).then(reloadDataTableTour);
  };

  _onShowingUnscheduleButtonClick = ({ listingId }) => {
    if (!global.confirm('Are you sure you want to un-schedule this showing?')) {
      return;
    }
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UNSCHEDULE_SHOWING,
      queryId: `${tour.id}-${listingId}`,
      payload: {
        tourId: tour.id,
        listingId: listingId,
      },
    })
      .then((apiRequestShowingResponsePayload) => {
        // (Re)load tour holding the newly requested showing, created by un-scheduling this showing.
        return reloadDataTableTour({
          tourIdToLoad: apiRequestShowingResponsePayload.requestShowingResult.id,
        });
      })
      .then(() => {
        if (tour.listings.length > 1) {
          return reloadDataTableTour();
        }

        return dispatch(actionDataTableRemoveItem, {
          dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
          entityId: tour.id,
        });
      });
  };

  _onCompletedButtonClick = () => {
    if (!global.confirm('Are you sure you want to mark this tour as "completed"?')) {
      return;
    }
    const { tour, reloadDataTableTour, dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_APPLY_TOUR_STATE_CHANGE,
      payload: {
        entityId: tour.id,
        toState: TOUR_STATUS_COMPLETED,
      },
    }).then(reloadDataTableTour);
  };

  _onDragEnd = (dragResult) => {
    if (!dragResult.source || !dragResult.destination) {
      return;
    }

    const { tour } = this.props;
    const { newShowingsOrder } = this.state;
    const initialIndex = dragResult.source.index;
    const newIndex = dragResult.destination.index;

    if (initialIndex === newIndex) {
      return;
    }
    const showingsClone = newShowingsOrder ? [...newShowingsOrder] : [...tour.listings];

    const reorderedNewShowingsOrder = reorderArrayElement(showingsClone, initialIndex, newIndex);

    const orderIsSameAsOriginal = reorderedNewShowingsOrder.every(({ id }, index) => {
      return tour.listings[index] && tour.listings[index].id === id;
    });

    this.setState({
      newShowingsOrder: orderIsSameAsOriginal
        ? null
        : reorderArrayElement(showingsClone, initialIndex, newIndex),
    });
  };

  _onShowingOrderConfirm = () => {
    const { tour, reloadDataTableTour, dispatch } = this.props;
    const { newShowingsOrder } = this.state;

    const updatedShowingPositions = newShowingsOrder.reduce(
      (acc, listing, index) => ({
        ...acc,
        [listing.id]: index,
      }),
      {},
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_SHOWING_POSITIONS,
      queryId: `${tour.id}`,
      payload: {
        tourId: tour.id,
        updatedShowingPositions: updatedShowingPositions,
      },
    })
      .then(reloadDataTableTour)
      .then(() => {
        if (!this._isUnmounting) {
          this.setState({
            newShowingsOrder: null,
          });
        }
      });
  };

  _calculateTourRoute = () => {
    const { tour, dispatch, reloadDataTableTour } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_CALCULATE_TOUR_ROUTE,
      queryId: `${tour.id}`,
      payload: {
        tourId: tour.id,
      },
    }).then((tourRoute) => {
      this.setState({ tourRoute });
      return reloadDataTableTour();
    });
  };

  _onHideTourRouteClick = () => {
    this.setState({ tourRoute: null });
  };

  render() {
    const {
      tour,
      reloadDataTableTour,
      onSaveForLaterButtonClick,
      dispatch,
      updateShowingPositionApiState,
      calculateTourRouteApiState,
      onMakeAvailableShowingButtonClick,
      onNotAvailableShowingButtonClick,
      onShowingMarkReadButtonClick,
      onOpenShowingNotes,
      onOpenMessageTenant,
      onOpenRenterPrequal,
      appUrl,
      tourAgents,
      householdAdminsList,
    } = this.props;

    const { newShowingsOrder, tourRoute } = this.state;

    const { user, renterProfile } = tour;

    const isPrequalified = isTourUserPrequalCompleted(tour);
    const userFrontConversation = tour.userFrontConversation;

    const agentUser = tour.agentUser;
    const householdAssignedAdminUserId = tour.household && tour.household.assignedAdminUserId;
    const googleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
      listings: tour.listings,
      showings: tour.showings,
    });

    return (
      <div
        className={classnames(
          'tourCard scheduled',
          newShowingsOrder && 'draggableActive',
          updateShowingPositionApiState.inProgress && 'draggableLoading',
          `renter-${user.phone}`,
        )}
      >
        {/* Top info bar */}
        <div className="topInfo">
          <div>
            <div>
              <span className="renterNameInfo">
                <span
                  onClick={() => onOpenRenterPrequal(user.id)}
                  className={classnames('renterName', {
                    isPrequalified: isPrequalified,
                  })}
                >
                  {user.name}
                </span>
                {tour.markedNeedsReschedulingFlag && <span> (Rescheduled)</span>}
              </span>
              <SrBadge>{tour.market && tour.market.displayName}</SrBadge>
            </div>
            <div className="subhead">
              {userFrontConversation && (
                <SrFrontConversation
                  frontConversation={userFrontConversation}
                  user={user}
                  dispatch={dispatch}
                />
              )}
              {userFrontConversation && (
                <SrLink
                  className="linkOut front"
                  target="_blank"
                  to={`https://app.frontapp.com/open/${userFrontConversation.conversationId}`}
                >
                  Front
                </SrLink>
              )}
              {googleMapsDirectionsUrl && (
                <SrLink className="linkOut gmaps" target="_blank" to={googleMapsDirectionsUrl}>
                  Maps
                </SrLink>
              )}
              <SrLink
                className="linkOut household"
                target="_blank"
                to={`${appUrl}/admin/household/all/${user.id}`}
              >
                Household
              </SrLink>
              <span className="linkOut automate" onClick={this._calculateTourRoute}>
                Automate
              </span>
              <SrLink
                className="linkOut addToTour"
                target="_blank"
                to={`${appUrl}/admin/tours/${tour.id}/add-to-tour`}
              >
                Add
              </SrLink>
            </div>
            <div className="subhead">
              <span className="detail">
                {renterProfile
                  ? getApplicationMovePeriodFormat(renterProfile)
                  : `${momentWithAustinTimezone(tour.renterMoveInDate).format(
                      'MM/DD/YYYY',
                    )} Move-in Date`}
              </span>
              <span className="detail additional">
                <SrCopyText formattedText={`Phone ${user.phone}`} text={user.phone} />
              </span>
              <span className="detail additional">
                {tour.chosenDates
                  .map((timeDate, i) => {
                    const format = i === 0 ? 'MMM Do: h:mma' : 'h:mma';
                    return momentWithAustinTimezone(timeDate).format(format);
                  })
                  .join(', ')}
              </span>
            </div>
          </div>
          <div className="timestampContainer">
            <span className="timestamp">
              {tour.type === TOUR_TYPE_SELF_SHOWING ? 'Self Tour | ' : ''}
              {agentUser
                ? `${agentUser.name} scheduled for ${moment(tour.startDate).format(
                    'h:mmA [on] MM/DD/YYYY',
                  )}`
                : `Scheduled for ${moment(tour.startDate).format('h:mmA [on] MM/DD/YYYY')}`}
            </span>
          </div>
        </div>

        {calculateTourRouteApiState.inProgress && <div className="adminLoadingGif" />}

        {!calculateTourRouteApiState.inProgress && tourRoute && (
          <SrAdminTourCardRoute {...tourRoute} onClose={this._onHideTourRouteClick} />
        )}

        {/* Table listing info section */}
        <div className="listingTable">
          {updateShowingPositionApiState.inProgress ? (
            // ? 'Please wait...'
            <div className="adminLoadingGif" />
          ) : (
            <DragDropContext onDragEnd={this._onDragEnd}>
              <table>
                <thead>
                  <tr>
                    <th className="listing">
                      <SrTourCopyAllAddressesHeader
                        user={user}
                        listings={tour.listings}
                        showings={tour.showings}
                      />
                    </th>
                    <th className="status">status</th>
                    <th className="availableDate">availability</th>
                    <th className="mls">
                      <SrTourCopyAllMlsNumbersHeader
                        listings={tour.listings}
                        showings={tour.showings}
                      />
                    </th>
                    <th className="agent">agent</th>
                    <th className="notes">notes</th>
                    <th className="actions">listing actions</th>
                  </tr>
                </thead>
                <Droppable droppableId={`droppable-${tour.id}`} direction="vertical">
                  {(dropProvided) => (
                    <tbody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      {(newShowingsOrder || tour.listings).map((listing, index) => {
                        const showing = tour.showings.find(
                          ({ listingId }) => listingId === listing.id,
                        );
                        const showingDisqualifications = getShowingDisqualifications({
                          tour,
                          listing,
                        });
                        return (
                          <tr
                            key={listing.id}
                            className={classnames(index % 0 ? undefined : '', {
                              notAvailable: showing.status === TOUR_SHOWING_STATUS_FAILED,
                              unread: showing.status === TOUR_SHOWING_STATUS_UNREAD,
                              disqualified:
                                showing.status !== TOUR_SHOWING_STATUS_UNREAD &&
                                showingDisqualifications.isDisqualified,
                            })}
                          >
                            <Draggable
                              draggableId={`draggable-tour-${tour.id}-listing-${listing.id}`}
                              index={index}
                            >
                              {(dragProvided) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                >
                                  <SrAdminTourCardListingCell listing={listing} showing={showing} />
                                  <td className="status">
                                    {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                                      ? listing.status
                                      : listing.mlsStatus}
                                  </td>
                                  <td className="availableDate">
                                    {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT ? (
                                      <SrLink
                                        to={getListingSmartApartmentLink(listing)}
                                        target="_blank"
                                      >
                                        View Floorplans
                                      </SrLink>
                                    ) : listing.availableDate ? (
                                      moment.utc(listing.availableDate).format('MMMM Do, YYYY')
                                    ) : (
                                      '--'
                                    )}
                                  </td>
                                  <td className="mls">
                                    {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                                      ? 'Smart'
                                      : <SrTourCopyMlsNumber mlsNumber={listing.mlsNumber} /> ||
                                        '--'}
                                  </td>
                                  {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT ? (
                                    <td className="agent">
                                      <span className="tooltip">{listing.apartmentName}</span>
                                      {listing.apartmentPhone || '--'}
                                    </td>
                                  ) : (
                                    <td className="agent">
                                      <span className="tooltip">{listing.listingAgent}</span>
                                      {listing.listingAgentCellPhone ||
                                        listing.listingAgentDirectWorkPhone ||
                                        '--'}
                                    </td>
                                  )}
                                  <SrAdminTourNotes
                                    showing={showing}
                                    listing={listing}
                                    onOpenShowingNotes={onOpenShowingNotes}
                                  />
                                  <td className="actions">
                                    <div
                                      className={classnames('listingActionButton', {
                                        confirmed: showing.status === TOUR_SHOWING_STATUS_COMPLETED,
                                        notAvailable: showing.status === TOUR_SHOWING_STATUS_FAILED,
                                      })}
                                    >
                                      {showing.status === TOUR_SHOWING_STATUS_UNREAD ? (
                                        <div className="confirmContainer">
                                          <span
                                            className="mainAction unread"
                                            onClick={() => {
                                              onShowingMarkReadButtonClick({
                                                listingId: listing.id,
                                              });
                                            }}
                                          >
                                            mark as read
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="confirmContainer">
                                          {activeShowingStatuses.includes(showing.status) ? (
                                            <span className="mainAction">
                                              {getTourShowingLabel({
                                                tourShowingStatus: showing.status,
                                                hasNote: showing.note,
                                              })}
                                              {showing.note && (
                                                <span className="tooltip">{showing.note}</span>
                                              )}
                                            </span>
                                          ) : (
                                            <span
                                              className="mainAction"
                                              onClick={() =>
                                                this._onShowingCompletedButtonClick({
                                                  listingId: listing.id,
                                                })
                                              }
                                            >
                                              success
                                            </span>
                                          )}
                                          <div className="arrowDropDown" />
                                          <div className="dropdown">
                                            <span
                                              className="option"
                                              onClick={() => onOpenMessageTenant(listing.id)}
                                            >
                                              message tenant
                                            </span>
                                            {showing.status === TOUR_SHOWING_STATUS_FAILED && (
                                              <span
                                                className="option confirm"
                                                onClick={() =>
                                                  this._onShowingCompletedButtonClick({
                                                    listingId: listing.id,
                                                  })
                                                }
                                              >
                                                success
                                              </span>
                                            )}
                                            <span
                                              className="option"
                                              onClick={() => onOpenShowingNotes(listing.id)}
                                            >
                                              add notes
                                            </span>
                                            {tour.showings.length > 1 && (
                                              <span
                                                className="option reschedule"
                                                onClick={() =>
                                                  this._onShowingUnscheduleButtonClick({
                                                    listingId: listing.id,
                                                  })
                                                }
                                              >
                                                unschedule
                                              </span>
                                            )}
                                            {showing.status === TOUR_SHOWING_STATUS_FAILED ? (
                                              <span
                                                className="option delete"
                                                onClick={() =>
                                                  onMakeAvailableShowingButtonClick({
                                                    listingId: listing.id,
                                                  })
                                                }
                                              >
                                                make available
                                              </span>
                                            ) : (
                                              <span
                                                className="option delete"
                                                onClick={() =>
                                                  onNotAvailableShowingButtonClick({
                                                    listingId: listing.id,
                                                  })
                                                }
                                              >
                                                not available
                                              </span>
                                            )}
                                            <span
                                              className="option saveForLater"
                                              onClick={() =>
                                                onSaveForLaterButtonClick({
                                                  listing: listing,
                                                })
                                              }
                                            >
                                              save for later
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  {showing.status !== TOUR_SHOWING_STATUS_UNREAD &&
                                    showingDisqualifications.disqualifications.length > 0 && (
                                      <div className="showingDisqualifications">
                                        <div className="showingDisqualificationsTitle">
                                          <span>Disqualifications:</span>
                                        </div>
                                        <ul>
                                          {showingDisqualifications.disqualifications.map(
                                            (disqualification) => {
                                              return (
                                                <li
                                                  className="showingDisqualificationsRow"
                                                  key={`${listing.id}-${disqualification}`}
                                                >
                                                  <span>{disqualification}</span>
                                                </li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                </div>
                              )}
                            </Draggable>
                          </tr>
                        );
                      })}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
          )}
          {newShowingsOrder && (
            <button
              className={classnames(
                'button',
                updateShowingPositionApiState.inProgress && 'disabled',
              )}
              type="button"
              onClick={this._onShowingOrderConfirm}
            >
              {updateShowingPositionApiState.inProgress ? 'saving...' : 'save order'}
            </button>
          )}
        </div>

        <div className="notesContainer">
          <span className="notesTitle">notes</span>

          <SrHouseholdNotesContainer
            household={tour.household}
            reloadDataTable={reloadDataTableTour}
          />
        </div>

        {/* Footer actions */}
        <div className="bottomActions clearfix">
          <div className="mainActionsContainer">
            <div className="deleteContainer">
              <span className="markDead">Archive</span>
              <SrAdminTourCardDeleteDropdown
                tour={tour}
                reloadDataTableTour={reloadDataTableTour}
                dispatch={dispatch}
              />
            </div>
            <div>
              <button
                className={classnames(
                  'button blue mainAction',
                  (tour.showings.some(({ status }) => !activeShowingStatuses.includes(status)) ||
                    newShowingsOrder) &&
                    'disabled',
                )}
                type="button"
                onClick={this._onCompletedButtonClick}
              >
                completed
              </button>
              <SrAdminTourCardSelectStartDate
                tour={tour}
                reloadDataTableTour={reloadDataTableTour}
                dispatch={dispatch}
              />
              <SrAdminTourCardSelectTourAgent
                tour={tour}
                tourAgents={tourAgents}
                reloadDataTableTour={reloadDataTableTour}
                dispatch={dispatch}
              />
              <SrTourRescheduleLink
                tour={tour}
                listingId={tour.listings[0].id}
                reloadDataTableTour={reloadDataTableTour}
              />
            </div>
          </div>
          {tour.household && householdAdminsList && (
            <div className="assignedAdminContainer">
              <SrAssignHouseholdAdminSelect
                householdId={tour.household.id}
                assignedAdminUserId={householdAssignedAdminUserId}
                reloadDataTable={reloadDataTableTour}
                householdAdminsList={householdAdminsList}
                dispatch={dispatch}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

SrAdminTourCardScheduled.displayName = 'SrAdminTourCardScheduled';

export default SrAdminTourCardScheduled;
